import { ChevronDown } from '@carbon/icons-react';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'component/translation';
import { forwardRef } from 'react';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { makeTestID, t } from 'util/stdtypes';

import styles from './sort-button.module.css';

/** @public */
export type SortButtonProps = Omit<ButtonProps, 'children'> & {
    text?: string;
    testID?: string;
};

export const SortButton = forwardRef<HTMLButtonElement, SortButtonProps>(({ text, testID, className, ...rest }, ref) => {
    // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
    const composedClassName = composeRenderProps(className, (callerClass) => cx(styles.button, callerClass));
    // Default aria-label describes the button's purpose + current state so
    // screen readers announce "Sort by Most popular" rather than just "Most
    // popular". Callers can override via `aria-label` (passed in rest).
    const defaultAriaLabel = useTranslation(t('sort_button.aria_label', 'Sort by {text}'), { text: text ?? '' });
    return (
        <Button aria-label={defaultAriaLabel} data-testid={makeTestID({ testID, text })} ref={ref} {...rest} className={composedClassName}>
            {text && <span>{text}</span>}
            <span aria-hidden className={styles.chevron}>
                <ChevronDown size={16} />
            </span>
        </Button>
    );
});
SortButton.displayName = 'SortButton';
